<template>
  <div class="container">
    <div class="row justify-content-center align-items-center min-vh-100">
      <div class="col-md-6 col-lg-4">
        <div class="card shadow-sm">
          <div class="card-body p-5" v-if="!this.$root.user">
            <!-- Logo e Titolo -->
            <div class="text-center mb-4">
              <i class="bi bi-shield-lock text-primary" style="font-size: 3rem;"></i>
              <h4 class="mt-2">Gestionale Pareri Admin</h4>
              <p class="text-muted">Accedi al pannello di amministrazione</p>
            </div>

            <!-- Form di Login -->
            <form @submit.prevent="handleLogin">
              <!-- Email -->
              <div class="form-floating mb-3">
                <input 
                  type="email" 
                  class="form-control" 
                  id="email" 
                  v-model="email"
                  placeholder="nome@esempio.com"
                  required
                >
                <label for="email">Email</label>
              </div>

              <!-- Password -->
              <div class="form-floating mb-4">
                <input 
                  :type="showPassword ? 'text' : 'password'" 
                  class="form-control" 
                  id="password" 
                  v-model="password"
                  placeholder="Password"
                  required
                >
                <label for="password">Password</label>
                <!-- Toggle Password Visibility -->
                <button 
                  type="button" 
                  class="btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none"
                  style="z-index: 5"
                  @click="togglePassword"
                >
                  <i class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
                </button>
              </div>

              <!-- Remember Me -->
              <div class="form-check mb-3">
                <input class="form-check-input" type="checkbox" v-model="rememberMe" id="rememberMe">
                <label class="form-check-label" for="rememberMe">
                  Ricordami
                </label>
              </div>

              <!-- Error Message -->
              <div v-if="error" class="alert alert-danger py-2 mb-3">
                {{ error }}
              </div>

              <!-- Submit Button -->
              <button 
                type="submit" 
                class="btn btn-primary w-100 py-2"
                :disabled="isLoading"
              >
                <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                {{ isLoading ? 'Accesso in corso...' : 'Accedi' }}
              </button>
            </form>

            <!-- Password Dimenticata -->
            <div class="text-center mt-3">
              <a href="#" class="text-decoration-none" @click.prevent="showForgotPassword = true">
                Password dimenticata?
              </a>
            </div>

            <!-- Form Reset Password -->
            <form v-if="showForgotPassword" @submit.prevent="handleForgotPassword" class="mt-3">
              <div class="border-top pt-3">
                <h5 class="mb-3">Recupera password</h5>
                <div class="form-floating mb-3">
                  <input 
                    type="email" 
                    class="form-control" 
                    id="resetEmail" 
                    v-model="resetEmail"
                    placeholder="nome@esempio.com"
                    required
                  >
                  <label for="resetEmail">Email</label>
                </div>

                <div v-if="resetError" class="alert alert-danger" role="alert">
                  {{ resetError }}
                </div>

                <div v-if="resetSuccess" class="alert alert-success" role="alert">
                  {{ resetSuccess }}
                </div>

                <div class="d-flex gap-2">
                  <button 
                    type="button" 
                    class="btn btn-outline-secondary" 
                    @click="showForgotPassword = false"
                  >
                    Annulla
                  </button>
                  <button 
                    type="submit" 
                    class="btn btn-primary flex-grow-1" 
                    :disabled="isResetting"
                  >
                    <span v-if="isResetting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    {{ isResetting ? 'Invio in corso...' : 'Invia email' }}
                  </button>
                </div>
              </div>
            </form>


          </div>
          <div class="card-body p-5" v-else-if="this.$root.user && !this.$root.user.confirmed">
            <div class="text-center mb-4">
              <i class="bi bi-shield-lock text-primary" style="font-size: 3rem;"></i>
              <h4 class="mt-2">Verifica autenticazione a due fattori</h4>
              <p class="text-muted">Inserisci il codice generato dalla tua app di autenticazione</p>
            </div>
            <div v-if="error" class="alert alert-danger" role="alert">
              {{ error }}
            </div>
            <form @submit.prevent="handleToken">
              <div class="form-floating mb-3">
                <input 
                  type="text" 
                  class="form-control" 
                  id="token" 
                  v-model="token"
                  placeholder="Codice di verifica"
                  required
                  autocomplete="off"
                  pattern="[0-9]*"
                  inputmode="numeric"
                  maxlength="6"
                >
                <label for="token">Codice di verifica</label>
              </div>
              <button type="submit" class="btn btn-primary w-100 py-2">
                Verifica
              </button>
              <a @click.prevent="forgot" class="btn btn-link text-center text-decoration-none w-100">Annulla</a>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { getCurrentInstance } from 'vue';
//import {authService} from '../../services/api'

export default {
  name: 'LoginView',
  emits: ['login-success'],
  setup(props, { emit }) {
    const email = ref('')
    const password = ref('')
    const rememberMe = ref(false)
    const showPassword = ref(false)
    const isLoading = ref(false)
    const error = ref('')
    const showForgotPassword = ref(false)
    const resetEmail = ref('')
    const isResetting = ref(false)
    const resetError = ref('')
    const resetSuccess = ref('')
    const token = ref('')

    const { appContext } = getCurrentInstance()
    const authService = appContext.config.globalProperties.$authService

    const togglePassword = () => {
      showPassword.value = !showPassword.value
    }

    return {
      email,
      password,
      rememberMe,
      showPassword,
      isLoading,
      error,
      togglePassword,
      showForgotPassword,
      resetEmail,
      isResetting,
      resetError,
      resetSuccess,
      token
    }
  },
  methods: {
    async handleLogin() {
      try {
        this.isLoading = true
        this.error = ''

        const { token, user } = await this.$root.$authService.login(this.email, this.password)

        //const { token, user } = await appContext.config.globalProperties.$authService.login(this.email, this.password)
        
        if (this.rememberMe) {
          localStorage.setItem('rememberMe', 'true')
          localStorage.setItem('savedEmail', this.email)
        } else {
          localStorage.removeItem('rememberMe')
          localStorage.removeItem('savedEmail')
        }

        // Emetti l'evento di login riuscito
        this.$emit('login-success', user)

      } catch (err) {
        console.error(err)
        this.error= err.response?.data?.errors?.email ? err.response?.data?.errors?.email[0] : 'Si è verificato un errore durante l\'accesso'
        this.isLoading = false;
      }
    },
    async handleForgotPassword() {
      try {
        this.isResetting = true
        this.resetError = ''
        this.resetSuccess = ''

        const response = await this.$root.$authService.forgotPassword(this.resetEmail)
        this.resetSuccess = response.message
        
      } catch (err) {
        this.resetError = err.response?.data?.errors?.email ? err.response?.data?.errors?.email[0] : 'Si è verificato un errore durante l\'invio dell\'email'
      } finally {
        this.isResetting = false
      }
    },
    async forgot() {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      this.$root.user = null
      this.loading = false;
    },
    async handleToken() {
      this.isLoading = true;
      this.error = null;
      
      try {
        // Invia il token 2FA al backend per la verifica
        const response = await this.$root.$api.post('/auth/2fa/confirm', { code: this.token });
        if (response.data.access_token) {
          await this.$authService.updateToken(response.data);
          this.$root.user.confirmed = true;
          this.$root.user.two_factor_verified = true;
          this.$emit('login-success', { user: this.$root.user });
        }
      } catch (error) {
        console.error('Errore nella verifica del token 2FA:', error);
        this.error = error.response?.data?.message || 'Codice di verifica non valido. Riprova.';
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.form-floating > .form-control:focus ~ label,
.form-floating > .form-control:not(:placeholder-shown) ~ label {
  opacity: .65;
  transform: scale(.85) translateY(-0.5rem) translateX(0.15rem);
}

.form-control:focus {
  box-shadow: 0 0 0 0.25rem rgba(13,110,253,.15);
}

.btn-link {
  color: #6c757d;
}

.btn-link:hover {
  color: #0d6efd;
}
</style>
